import { formatDateForAPI, lastMonthsRange } from "@/lib/date-utils";
import {
  AGREEMENT_FILTER_OPTIONS,
  ALL_AGREEMENTS,
  type AgreementFilterValue,
} from "@/services/admin-agreement.service";
import { UserStatus } from "@/types/admin";
import { createTableFiltersStore } from "./createTableFiltersStore";

/**
 * Statuses the user list filters by. `All` is the cleared state, not a backend value.
 *
 * Only two of the four UserStatus members are offered, matching the control the operator sees:
 * PendingVerification and Deleted are absent from the dropdown, so a cached one would filter the
 * table down to rows the UI cannot explain — which is what `sanitize` below exists to prevent.
 */
export const USER_STATUS_OPTIONS: {
  value: UserStatus | "All";
  label: string;
}[] = [
  { value: "All", label: "All Statuses" },
  { value: UserStatus.Active, label: "Active" },
  { value: UserStatus.Suspended, label: "Suspended" },
];

export interface UserListFilters extends Record<string, unknown> {
  /** Free-text term: a whole user ID, or a fragment of a name, e-mail or phone number. */
  search: string;

  status: UserStatus | "All";

  /**
   * Registered-at range, already formatted for the API: the picked calendar day carrying this
   * client's UTC offset (`2026-08-21T00:00:00+07:00`). The server expands each end to the whole
   * of that day in the caller's frame — see `AdminUserQueryService`.
   */
  registeredFrom?: string;
  registeredTo?: string;

  /** Commitment and whether the user agreed. Sent as agreement / agreementAccepted. */
  agreement: AgreementFilterValue;
}

/**
 * The registered-at range defaults to the last six months rather than to "no filter": the list opens
 * on a bounded window, and the operator widens or clears it from the picker. Computed once per page
 * load, so a tab left open for days keeps the window it started with until the filters are reset.
 *
 * Unlike the other three stores, this one holds the two ends already formatted for the API, because
 * that is what UserFilters puts in and what `useUsers` sends — so the shared range is passed through
 * the same `formatDateForAPI` the picker's onChange uses.
 */
const defaultRange = lastMonthsRange();

const defaults: UserListFilters = {
  search: "",
  status: "All",
  registeredFrom: formatDateForAPI(defaultRange.from),
  registeredTo: formatDateForAPI(defaultRange.to),
  agreement: ALL_AGREEMENTS,
};

/**
 * Filter and paging state for the admin user list, cached in sessionStorage.
 *
 * No `defaultSort`: `GetUsersQuery` has no sort parameters and `AdminUserQueryService` always
 * orders by registration date descending. The store this replaces carried a sortBy/sortOrder pair
 * that only ever changed the query key — a new cache entry and a refetch for an identical result.
 *
 * Bump `version` in step with any change to the shape above or to the status options.
 */
export const useUserFiltersStore = createTableFiltersStore<UserListFilters>({
  name: "admin.users.filters",
  version: 3,
  defaults,
  sanitize: (filters) => ({
    ...filters,

    // sessionStorage outlives a release: a status that is no longer offered must not survive as a
    // filter the operator cannot see in the UI, and so cannot clear from it.
    status: USER_STATUS_OPTIONS.some(
      (option) => option.value === filters.status,
    )
      ? filters.status
      : defaults.status,

    // Same reason as status: a commitment choice no longer offered falls back to "all".
    agreement: AGREEMENT_FILTER_OPTIONS.some(
      (option) => option.value === filters.agreement,
    )
      ? filters.agreement
      : defaults.agreement,

    // No half-range rule here, unlike the other three stores: AdminUserQueryService applies each
    // end independently, so a from-only range really is filtering and must be left as the operator
    // set it.
  }),
});
